package com.arena_web.ui.sections.profile

import androidx.compose.runtime.*
import com.arena_web.data.dto.ChartDataPointDto
import com.arena_web.data.dto.StatsChartDto
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.TimeSwitcher
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

private fun periodLabels(period: String): List<String> = when (period) {
    "year" -> listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    "month" -> listOf("3", "6", "9", "12", "15", "18", "21", "24", "27", "30")
    "week" -> listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")
    else -> listOf("3", "6", "9", "12", "15", "18", "21", "24", "27", "30")
}

private val shortMonthNames = listOf(
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
)

private val dayOfWeekNames = listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")

private fun formatLabel(raw: String, period: String): String = when (period) {
    "year" -> {
        // "2025-03" → "Mar"
        val month = raw.substringAfter("-").toIntOrNull()
        if (month != null && month in 1..12) shortMonthNames[month - 1] else raw
    }
    "month" -> {
        // "2025-03-24" → "24"
        raw.substringAfterLast("-").trimStart('0').ifEmpty { "0" }
    }
    "week" -> {
        // "2025-03-19" → day of week via JS Date
        try {
            val d = kotlin.js.Date(raw)
            val dow = d.getDay().toInt() // 0=Sun
            dayOfWeekNames[if (dow == 0) 6 else dow - 1]
        } catch (_: Exception) { raw }
    }
    else -> raw
}

internal fun resolveDataPoints(
    apiPoints: List<ChartDataPointDto>?,
    period: String
): List<ChartDataPointDto> {
    if (!apiPoints.isNullOrEmpty()) {
        return apiPoints.map { it.copy(label = formatLabel(it.label, period)) }
    }
    return periodLabels(period).map {
        ChartDataPointDto(label = it, stats = emptyMap())
    }
}

private val monthNames = listOf(
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
)

@Composable
private fun TooltipBadge(value: Int, iconSrc: String?, iconSize: Number = 17) {
    // Figma: bg #112140, rounded-28, px-12 py-4, h-24; pointer 8.107×6.633 below
    Box(
        modifier = Modifier
            .sBorderRadius(28)
            .sHeight(24)
            .styleModifier {
                property("padding", "${s(4)} ${s(12)}")
                property("background", "var(--arena-surface-variant)")
                property("white-space", "nowrap")
                // Shrink-wrap to content so the pill grows horizontally
                // for multi-digit values instead of staying single-digit
                // wide and clipping the number.
                property("width", "max-content")
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
            },
        contentAlignment = Alignment.Center
    ) {
        Row(
            modifier = Modifier.sGap(4),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                "$value",
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(24)
                    .sLetterSpacing(0.16)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            if (iconSrc != null) {
                Icon(src = iconSrc, modifier = Modifier.sSize(iconSize))
            }
        }
    }
    // Pointer triangle below
    Box(
        modifier = Modifier.styleModifier {
            property("width", "0")
            property("height", "0")
            property("border-left", "${s(3.3)} solid transparent")
            property("border-right", "${s(3.3)} solid transparent")
            property("border-top", "${s(8.1)} solid #112140")
        }
    )
}

@Composable
private fun chartTitle(statKey: String, period: String): String {
    val t = LocalAppStrings.current
    val stat = if (statKey == "goals") t.profileStatsChartsGoals else t.profileStatsChartsAssists
    val per = when (period) {
        "year" -> t.profileStatsChartsPerYear
        "month" -> monthNames[kotlin.js.Date().getMonth()]
        "week" -> t.profileStatsChartsPerWeek
        else -> t.profileStatsChartsPerMonth
    }
    return t.profileStatsChartsTitle(stat, per)
}

@Composable
fun StatsChartsSection(
    statsCharts: Map<String, StatsChartDto> = emptyMap(),
    goalsChartPeriod: String,
    assistsChartPeriod: String,
    onGoalsPeriodChanged: (String) -> Unit,
    onAssistsPeriodChanged: (String) -> Unit,
    compact: Boolean = false,
    modifier: Modifier = Modifier
) {
    val goalsData = resolveDataPoints(statsCharts[goalsChartPeriod]?.dataPoints, goalsChartPeriod)
    val assistsData = resolveDataPoints(statsCharts[assistsChartPeriod]?.dataPoints, assistsChartPeriod)

    Column(
        modifier = modifier.fillMaxWidth()
            .then(if (compact) Modifier.gap(16.px) else Modifier.fillMaxHeight().sGap(20))
    ) {
        ChartCard(
            title = chartTitle("goals", goalsChartPeriod),
            chartPeriod = goalsChartPeriod,
            onPeriodChanged = onGoalsPeriodChanged,
            compact = compact
        ) {
            BarChart(
                dataPoints = goalsData,
                statKey = "goals",
                period = goalsChartPeriod
            )
        }

        ChartCard(
            title = chartTitle("assists", assistsChartPeriod),
            chartPeriod = assistsChartPeriod,
            onPeriodChanged = onAssistsPeriodChanged,
            compact = compact
        ) {
            LineChart(
                dataPoints = assistsData,
                statKey = "assists",
                period = assistsChartPeriod
            )
        }
    }
}

@Composable
private fun ChartCard(
    title: String,
    chartPeriod: String,
    onPeriodChanged: (String) -> Unit,
    compact: Boolean = false,
    chart: @Composable () -> Unit
) {
    // Outer wrapper with backdrop blur + gradient
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .then(
                if (compact) Modifier
                else Modifier.sBorderRadius(40).styleModifier { property("flex", "1 1 0px") }
            )
            .overflow(Overflow.Hidden)
            .styleModifier {
                if (!compact) {
                    property("backdrop-filter", "blur(${s(11.165)})")
                    property("-webkit-backdrop-filter", "blur(${s(11.165)})")
                    property("background", "linear-gradient(177deg, rgba(60, 121, 241, 0.135) 13.9%, rgba(5, 4, 12, 0.03) 69.5%)")
                }
            }
    ) {
    // Inner card
    Column(
        modifier = Modifier
            .fillMaxSize()
            .then(if (compact) Modifier.borderRadius(16.px).padding(16.px).gap(8.px)
                  else Modifier.sBorderRadius(40).sPadding(16, 16).sPaddingBottom(18).sGap(8))
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property(
                    "background",
                    "linear-gradient(14deg, rgba(0, 59, 174, 0.03) 26.9%, rgba(3, 87, 230, 0.03) 67.5%)"
                )
            }
    ) {
        // Title + period switcher
        Row(
            modifier = Modifier.fillMaxWidth().then(if (compact) Modifier else Modifier.sPaddingLeft(8)),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                title,
                modifier = Modifier
                    .then(if (compact) Modifier.fontSize(16.px) else Modifier.sFontSize(24))
                    .fontWeight(AppTypography.Medium)
                    .then(if (compact) Modifier else Modifier.sLetterSpacing(0.24).sLineHeight(32))
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .flexGrow(1)
            )
            TimeSwitcher(chartPeriod, onPeriodChanged)
        }

        // Chart fills remaining space, content at bottom
        Box(
            modifier = Modifier.fillMaxWidth().styleModifier {
                if (!compact) {
                    property("flex", "1 1 0px")
                    property("display", "flex")
                    property("align-items", "flex-end")
                }
            }
        ) {
            chart()
        }
    } // inner card
    } // outer wrapper
}


// =============================================================================
// Bar Chart — styled divs with inner shadows (matches Figma 3D glow bars)
// =============================================================================

@Composable
internal fun BarChart(
    dataPoints: List<ChartDataPointDto>,
    statKey: String,
    period: String = "month",
    chartHeight: Int = 260,
    barWidthMultiplier: Double = 1.0,
    tooltipIcon: String? = null,
    tooltipIconSize: Number = 17
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val values = dataPoints.map { it.stats[statKey] ?: 0 }
    val maxValue = (values.maxOrNull() ?: 1).coerceAtLeast(1)
    val chartH = chartHeight
    val barW = ((when (period) {
        "week" -> 37
        "year" -> 28
        else -> 32
    }) * barWidthMultiplier).toInt()
    // Resolve bar shadow with s() scaling
    val barShadow = sportColors.barBoxShadow
        .replace(Regex("\\{([0-9.]+)\\}")) { s(it.groupValues[1].toDouble()) }

    if (dataPoints.isEmpty()) {
        Box(
            modifier = Modifier.fillMaxWidth().sHeight(150),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.profileStatsChartsNoData,
                modifier = Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
            )
        }
        return
    }

    key(period) {
    var hoveredBarIndex by remember { mutableStateOf(-1) }
    var animated by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        delay(50)
        animated = true
    }

    Column(
        modifier = Modifier.fillMaxSize().sPadding(0, 16)
            .styleModifier { property("justify-content", "flex-end") }
    ) {
        // Bars + labels row
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(chartH)
                .styleModifier { property("align-items", "flex-end") },
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            dataPoints.forEachIndexed { idx, dp ->
                val value = dp.stats[statKey] ?: 0
                val barH = (value.toDouble() / maxValue * chartH).coerceAtLeast(8.0).toInt()
                val animatedH = if (animated) barH else 0

                Column(
                    modifier = Modifier.sGap(10)
                        .styleModifier { property("position", "relative") },
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // Tooltip above bar
                    if (hoveredBarIndex == idx) {
                        Column(
                            modifier = Modifier
                                .styleModifier {
                                    property("position", "absolute")
                                    property("bottom", "100%")
                                    property("left", "50%")
                                    property("transform", "translateX(-50%)")
                                    property("z-index", "10")
                                    property("pointer-events", "none")
                                    property("margin-bottom", s(20))
                                },
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            TooltipBadge(value, tooltipIcon, tooltipIconSize)
                        }
                    }

                    Box(
                        modifier = Modifier
                            .sWidth(barW)
                            .overflow(Overflow.Hidden)
                            .styleModifier {
                                val r = s((barW / 2.0))
                                property("border-radius", "$r")
                            }
                            .cursor(Cursor.Pointer)
                            .onMouseEnter { hoveredBarIndex = idx }
                            .onMouseLeave { hoveredBarIndex = -1 }
                            .styleModifier {
                                property("height", s(animatedH.toDouble()))
                                property("border", "0.8px solid ${sportColors.barBorderColor}")
                                property("background", sportColors.barBackground)
                                property("backdrop-filter", "blur(${s(15)})")
                                property("-webkit-backdrop-filter", "blur(${s(15)})")
                                property("box-shadow", barShadow)
                                property("flex-shrink", "0")
                                property("transition", "height 0.8s ease-out, transform 0.2s ease")
                                if (hoveredBarIndex == idx) {
                                    if (barWidthMultiplier > 1.0) {
                                        property("transform", "scaleX(1.05) scaleY(1.02)")
                                    } else {
                                        property("transform", "scaleX(1.15) scaleY(1.05)")
                                    }
                                    property("transform-origin", "bottom center")
                                }
                            }
                    )
                    SpanText(
                        dp.label,
                        modifier = Modifier
                            .sFontSize(12)
                            .fontWeight(AppTypography.Medium)
                            .sLetterSpacing(0.012)
                            .sLineHeight(12)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .styleModifier { property("white-space", "nowrap") }
                    )
                }
            }
        }
    }
    } // key(period)
}

// =============================================================================
// Line Chart — HTML/CSS based (same pattern as BarChart for proper scaling)
// =============================================================================

@Composable
internal fun LineChart(
    dataPoints: List<ChartDataPointDto>,
    statKey: String,
    period: String = "month",
    chartHeight: Int = 220,
    tooltipIcon: String? = null,
    tooltipIconSize: Number = 17,
    /** When true, the tooltip only opens on click/tap (toggles off when
     *  the same point is tapped again, or moves to the new tapped point);
     *  mouse hover/leave is ignored. Used on touch/mobile so the tooltip
     *  doesn't track the finger during scroll. */
    clickOnly: Boolean = false
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current

    val values = dataPoints.map { it.stats[statKey] ?: 0 }
    val maxValue = (values.maxOrNull() ?: 1).coerceAtLeast(1)
    val chartH = chartHeight

    if (dataPoints.isEmpty()) {
        Box(
            modifier = Modifier.fillMaxWidth().sHeight(150),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.profileStatsChartsNoData,
                modifier = Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
            )
        }
        return
    }

    val chartLineRgb = sportColors.chartLineStroke.toRgb()
    val dataPointRgb = sportColors.dataPoint.toRgb()
    val dataPointInnerRgb = sportColors.dataPointInner.toRgb()

    // Normalized positions: x as percentage (0-100), y as percentage (0-100)
    val normPoints = dataPoints.mapIndexed { idx, dp ->
        val xPct = if (dataPoints.size > 1) idx.toDouble() / (dataPoints.size - 1) * 100 else 50.0
        val value = dp.stats[statKey] ?: 0
        val yPct = 100.0 - (value.toDouble() / maxValue * 100)
        Pair(xPct, yPct)
    }

    // Smooth cubic bezier path builder
    fun smoothPath(pts: List<Pair<Double, Double>>): String {
        if (pts.size < 2) return "M ${pts.first().first} ${pts.first().second}"
        val sb = StringBuilder("M ${pts.first().first} ${pts.first().second}")
        for (i in 1 until pts.size) {
            val prev = pts[i - 1]
            val curr = pts[i]
            val tension = 0.3
            val dx = curr.first - prev.first
            val cp1x = prev.first + dx * tension
            val cp1y = prev.second
            val cp2x = curr.first - dx * tension
            val cp2y = curr.second
            sb.append(" C $cp1x $cp1y, $cp2x $cp2y, ${curr.first} ${curr.second}")
        }
        return sb.toString()
    }

    val linePath = smoothPath(normPoints)
    val areaPath = "$linePath L ${normPoints.last().first} 100 L ${normPoints.first().first} 100 Z"

    val svgContent = buildString {
        append("<svg width=\"100%\" height=\"100%\" viewBox=\"0 0 100 100\" preserveAspectRatio=\"none\" xmlns=\"http://www.w3.org/2000/svg\">")
        append("<defs>")
        append("<linearGradient id=\"areaFill\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\">")
        append("<stop offset=\"0%\" stop-color=\"rgba(${chartLineRgb.red},${chartLineRgb.green},${chartLineRgb.blue},0.6)\"/>")
        append("<stop offset=\"100%\" stop-color=\"rgba(${chartLineRgb.red},${chartLineRgb.green},${chartLineRgb.blue},0.02)\"/>")
        append("</linearGradient>")
        append("<clipPath id=\"reveal\"><rect x=\"0\" y=\"0\" width=\"0\" height=\"100\"><animate attributeName=\"width\" from=\"0\" to=\"100\" dur=\"1s\" fill=\"freeze\" calcMode=\"spline\" keySplines=\"0.25 0.1 0.25 1\"/></rect></clipPath>")
        append("</defs>")
        append("<g clip-path=\"url(#reveal)\">")
        append("<path d=\"$areaPath\" fill=\"url(#areaFill)\"/>")
        append("<path d=\"$linePath\" fill=\"none\" stroke=\"rgb(${chartLineRgb.red},${chartLineRgb.green},${chartLineRgb.blue})\" stroke-width=\"3\" vector-effect=\"non-scaling-stroke\"/>")
        append("</g>")
        append("</svg>")
    }

    var hoveredIndex by remember { mutableStateOf(-1) }

    Column(
        modifier = Modifier.fillMaxSize().sPadding(0, 16)
            .styleModifier { property("justify-content", "flex-end") }
    ) {
        // Chart area
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(chartH)
                .styleModifier { property("position", "relative") }
        ) {
            // SVG line + area (key forces re-render on period/data change)
            key(svgContent + period) {
                Div(
                    attrs = {
                        ref { el -> el.innerHTML = svgContent; onDispose { } }
                        style {
                            property("position", "absolute")
                            property("inset", "0")
                        }
                    }
                )
            }

            // Invisible hover zones + visible dot on hover
            normPoints.forEachIndexed { idx, (xPct, yPct) ->
                val value = dataPoints[idx].stats[statKey] ?: 0
                // Hover/tap zone (wide invisible strip). In `clickOnly`
                // mode the badge toggles on tap (re-tapping the active
                // point closes it) so the tooltip doesn't shadow a scroll
                // gesture; otherwise it follows the cursor.
                Box(
                    modifier = Modifier
                        .styleModifier {
                            property("position", "absolute")
                            property("left", "$xPct%")
                            property("top", "0")
                            property("bottom", "0")
                            property("width", s(30))
                            property("transform", "translateX(-50%)")
                            property("cursor", "pointer")
                            property("z-index", "2")
                        }
                        .then(
                            if (clickOnly) {
                                Modifier.onClick {
                                    hoveredIndex = if (hoveredIndex == idx) -1 else idx
                                }
                            } else {
                                Modifier
                                    .onMouseEnter { hoveredIndex = idx }
                                    .onMouseLeave { hoveredIndex = -1 }
                            }
                        )
                )

                // Dashed vertical line (hover only)
                if (hoveredIndex == idx) {
                    Box(
                        modifier = Modifier
                            .styleModifier {
                                property("position", "absolute")
                                property("left", "$xPct%")
                                property("top", "$yPct%")
                                property("bottom", "0")
                                property("width", "1px")
                                property("transform", "translateX(-50%)")
                                property("border-left", "1px dashed rgba(255,255,255,0.4)")
                                property("pointer-events", "none")
                                property("z-index", "1")
                            }
                    )
                }

                // Data point dot (hover only)
                if (hoveredIndex == idx) {
                    // Outer glow
                    Box(
                        modifier = Modifier
                            .sSize(28)
                            .sBorderRadius(100)
                            .styleModifier {
                                property("position", "absolute")
                                property("left", "$xPct%")
                                property("top", "$yPct%")
                                property("transform", "translate(-50%, -50%)")
                                property("background", "radial-gradient(circle, rgba(${chartLineRgb.red},${chartLineRgb.green},${chartLineRgb.blue},0.5) 0%, rgba(${chartLineRgb.red},${chartLineRgb.green},${chartLineRgb.blue},0.1) 70%, transparent 100%)")
                                property("pointer-events", "none")
                                property("z-index", "3")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        // Inner dot
                        Box(
                            modifier = Modifier
                                .sSize(14)
                                .sBorderRadius(100)
                                .backgroundColor(sportColors.dataPointInner)
                        )
                    }

                    // Tooltip
                    Column(
                        modifier = Modifier
                            .styleModifier {
                                property("position", "absolute")
                                property("left", "$xPct%")
                                property("top", "${yPct - 12}%")
                                property("transform", "translate(-50%, -100%)")
                                property("z-index", "4")
                                property("pointer-events", "none")
                            },
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        TooltipBadge(value, tooltipIcon, tooltipIconSize)
                    }
                }
            }
        }

        // Dotted X-axis (absolute positioned to match chart points)
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(10)
                .styleModifier {
                    property("position", "relative")
                }
        ) {
            val smallDotsPerGap = 5
            normPoints.forEachIndexed { idx, (xPct, _) ->
                // Large dot at data position
                Box(
                    modifier = Modifier
                        .sSize(10)
                        .sBorderRadius(100)
                        .styleModifier {
                            property("position", "absolute")
                            property("left", "$xPct%")
                            property("top", "50%")
                            property("transform", "translate(-50%, -50%)")
                            property("background", "rgba(${chartLineRgb.red},${chartLineRgb.green},${chartLineRgb.blue},0.6)")
                        }
                )
                // Small dots between this point and the next
                if (idx < normPoints.size - 1) {
                    val nextXPct = normPoints[idx + 1].first
                    for (j in 1..smallDotsPerGap) {
                        val pct = xPct + (nextXPct - xPct) * j / (smallDotsPerGap + 1)
                        Box(
                            modifier = Modifier
                                .sSize(4)
                                .sBorderRadius(100)
                                .styleModifier {
                                    property("position", "absolute")
                                    property("left", "$pct%")
                                    property("top", "50%")
                                    property("transform", "translate(-50%, -50%)")
                                    property("background", "rgba(${chartLineRgb.red},${chartLineRgb.green},${chartLineRgb.blue},0.3)")
                                }
                        )
                    }
                }
            }
        }

        // Labels row
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sPaddingTop(8)
                .styleModifier {
                    property("position", "relative")
                    property("height", s(12))
                }
        ) {
            dataPoints.forEachIndexed { idx, dp ->
                val xPct = if (dataPoints.size > 1) idx.toDouble() / (dataPoints.size - 1) * 100 else 50.0
                Box(
                    modifier = Modifier
                        .styleModifier {
                            property("position", "absolute")
                            property("left", "$xPct%")
                            property("transform", "translateX(-50%)")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        dp.label,
                        modifier = Modifier
                            .sFontSize(12)
                            .fontWeight(AppTypography.Regular)
                            .sLetterSpacing(0.012)
                            .sLineHeight(12)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .styleModifier { property("white-space", "nowrap") }
                    )
                }
            }
        }
    }
}
